import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ReactElement } from 'react';
import { ConfirmDialog } from '@taki/ui';
import type { AvisDAction, ConfirmationAssemblee } from '@taki/ui';
import { useAppRuntime } from './runtime.js';
import {
  avisDeLancement,
  avisDeRefus,
  clefDeBouton,
  fenetreDesSorties,
  portDeBoutonsFluxDuSdk,
} from './boutons-flux.js';
import type { AvisDeBouton, BranchementsDeBoutons, PortDeBoutonsFlux, SortieDeBouton } from './boutons-flux.js';

/**
 * LE CONTROLEUR DES SMART BOUTONS FLUX d'un ecran : ce que les DEUX rangees (3a et
 * fiche) partagent. Le clic, l'etat « en cours », la fenetre des sorties hors de
 * l'equipe, l'avis final et la relecture apres un lancement vivent ici UNE fois ; les
 * rangees ne font que lui demander l'etat d'un bouton et lui passer le clic.
 *
 * LE PORT vient de l'hote (`AppRuntimeProvider`, `boutonsFlux`), a defaut du SDK
 * (`portDeBoutonsFluxDuSdk`). Sans port, tout bouton flux est grise.
 *
 * L'AVIS N'EST PLUS UNE FENETRE : c'est la pastille de la rangee 3 du gabarit
 * (`avisAction` de `ScreenLayout`, aux deux niveaux), « Action executee », avec
 * une croix, sans lien vers l'app Flux (beaucoup d'utilisateurs ne l'ont pas).
 * Elle reste jusqu'au prochain lancement ou au changement de fiche ou de porte
 * (`oublierLAvis`). Seule la confirmation des envois hors de l'equipe reste une
 * fenetre.
 *
 * APRES UN LANCEMENT, L'ECRAN RELIT CE QUE LE FLUX A ECRIT (`apresLancement`,
 * pose par l'ecran) : la fiche ouverte pour un bouton de fiche, le jeu de la
 * porte pour un bouton de collection. Le serveur a fini d'ecrire quand il
 * repond « lance » : c'est la valeur finale, sans rechargement de la page.
 */

/**
 * Le bouton qui clique : sa porte, son id, son libelle, et la confirmation
 * declaree. Le gabarit NE LA MONTRE PAS pour un bouton flux (la rangee la lui
 * retire) : c'est ici qu'elle s'ouvre, dans la fenetre unique qui porte aussi
 * les envois hors de l'equipe.
 */
export interface BoutonFlux {
  porteId: string;
  boutonId: string;
  label: string;
  confirmation?: ConfirmationAssemblee | undefined;
}

/** Ce que l'appel demande au serveur : le clic direct, l'apercu (rien ne part), ou le oui donne. */
type Appel = 'direct' | 'apercu' | 'confirme';

/** Ou en est le branchement d'un bouton : en lecture, aucun flux actif, ou au moins un. */
export type EtatDuBranchement = 'chargement' | 'aucun' | 'branche';

export interface ControleurDeBoutonsFlux {
  etat(porteId: string, boutonId: string): EtatDuBranchement;
  /** Un lancement est en cours pour ce bouton : pas de second clic. */
  occupe(porteId: string, boutonId: string): boolean;
  /**
   * Le clic. Avec une confirmation declaree : l'apercu du serveur, UNE fenetre
   * (la confirmation et les sorties), puis le lancement. Sans : le lancement
   * direct, et une fenetre des sorties seulement si le serveur en demande.
   * `ficheId` : la fiche ouverte, `null` en collection.
   */
  lancer(bouton: BoutonFlux, ficheId: string | null): void;
  /** La fenetre a rendre a cote du gabarit (les sorties a confirmer), `null` quand aucune. */
  fenetres: ReactElement | null;
  /** L'avis du dernier lancement, pour la prop `avisAction` du gabarit ; `null` quand aucun. */
  avisAction: AvisDAction | null;
  /** Efface l'avis : le changement de fiche ou de porte. */
  oublierLAvis(): void;
}

interface Attente {
  bouton: BoutonFlux;
  ficheId: string | null;
  sorties: SortieDeBouton[];
}

export function useBoutonsFlux(
  appId: string,
  actif: boolean,
  /** Appelee quand le serveur a rendu « lance » (flux termines, ou en pause, en echec) : `ficheId` null = bouton de collection. */
  apresLancement?: (ficheId: string | null) => void,
): ControleurDeBoutonsFlux {
  const { sdk, boutonsFlux } = useAppRuntime();
  const port = useMemo<PortDeBoutonsFlux | null>(() => boutonsFlux ?? portDeBoutonsFluxDuSdk(sdk), [boutonsFlux, sdk]);
  const apresRef = useRef(apresLancement);
  apresRef.current = apresLancement;

  const [branchements, setBranchements] = useState<BranchementsDeBoutons | null>(null);
  useEffect(() => {
    if (!actif || port === null) return;
    let vivant = true;
    const lire = (): void => {
      port.branchements(appId).then(
        (lus) => { if (vivant) setBranchements(lus); },
        (cause: unknown) => {
          console.warn('[taki/app-runtime] Les branchements des boutons flux sont illisibles.', cause);
          if (vivant) setBranchements({});
        },
      );
    };
    lire();
    /* UN FLUX CREE OU ACTIVE DANS FLUX se voit au retour sur l'onglet. */
    const auRetour = (): void => { if (document.visibilityState === 'visible') lire(); };
    document.addEventListener('visibilitychange', auRetour);
    return () => {
      vivant = false;
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, [actif, port, appId]);

  /* LES BOUTONS EN COURS : une REF pour la garde (valable dans le meme tick,
     avant tout rendu), un etat pour le `loading`. */
  const occupesRef = useRef<Set<string>>(new Set());
  const [occupes, setOccupes] = useState<ReadonlySet<string>>(new Set());
  const marquer = useCallback((cle: string, enCours: boolean): void => {
    if (enCours) occupesRef.current.add(cle);
    else occupesRef.current.delete(cle);
    setOccupes(new Set(occupesRef.current));
  }, []);

  const [attente, setAttente] = useState<Attente | null>(null);
  const [attenteOuverte, setAttenteOuverte] = useState(false);
  const [avis, setAvis] = useState<{ bouton: BoutonFlux; avis: AvisDeBouton } | null>(null);
  const vivantRef = useRef(true);
  useEffect(() => {
    vivantRef.current = true;
    return () => { vivantRef.current = false; };
  }, []);

  const appeler = useCallback(
    async (bouton: BoutonFlux, ficheId: string | null, appel: Appel): Promise<void> => {
      const cle = clefDeBouton(appId, bouton.porteId, bouton.boutonId);
      if (port === null || occupesRef.current.has(cle)) return;
      /* UN NOUVEAU LANCEMENT EFFACE L'AVIS D'AVANT. */
      setAvis(null);
      marquer(cle, true);
      try {
        const reponse = await port.declencher({
          appId,
          porteId: bouton.porteId,
          boutonId: bouton.boutonId,
          ...(ficheId === null ? {} : { ficheId }),
          ...(appel === 'confirme' ? { confirme: true } : {}),
          ...(appel === 'apercu' ? { apercu: true } : {}),
        });
        if (!vivantRef.current) return;
        if (reponse.statut === 'confirmation') {
          if (appel === 'confirme') {
            /* LE SERVEUR REDEMANDE ALORS QU'ON A DIT OUI : rien ne part. */
            setAvis({ bouton, avis: avisDeRefus(new Error('la confirmation n\'a pas été prise en compte')) });
            return;
          }
          setAttente({ bouton, ficheId, sorties: reponse.sorties });
          setAttenteOuverte(true);
          return;
        }
        setAvis({ bouton, avis: avisDeLancement(reponse.executions) });
        apresRef.current?.(ficheId);
      } catch (cause) {
        if (!vivantRef.current) return;
        setAvis({ bouton, avis: avisDeRefus(cause) });
      } finally {
        if (vivantRef.current) marquer(cle, false);
        else occupesRef.current.delete(cle);
      }
    },
    [appId, port, marquer],
  );

  const lancer = useCallback(
    (bouton: BoutonFlux, ficheId: string | null): void => {
      void appeler(bouton, ficheId, bouton.confirmation === undefined ? 'direct' : 'apercu');
    },
    [appeler],
  );

  const etat = useCallback(
    (porteId: string, boutonId: string): EtatDuBranchement => {
      if (port === null) return 'aucun';
      if (branchements === null) return 'chargement';
      return (branchements[clefDeBouton(appId, porteId, boutonId)]?.length ?? 0) > 0 ? 'branche' : 'aucun';
    },
    [port, branchements, appId],
  );
  const occupe = useCallback(
    (porteId: string, boutonId: string): boolean => occupes.has(clefDeBouton(appId, porteId, boutonId)),
    [occupes, appId],
  );

  const oublierLAvis = useCallback((): void => setAvis(null), []);

  const avisAction = useMemo<AvisDAction | null>(() => {
    if (avis === null) return null;
    return {
      ton: avis.avis.ton === 'fait' ? 'succes' : avis.avis.ton === 'neutre' ? 'neutre' : 'danger',
      texte: avis.avis.texte,
      ...(avis.avis.detail === undefined ? {} : { detail: avis.avis.detail }),
      onFermer: oublierLAvis,
    };
  }, [avis, oublierLAvis]);

  const fenetres = useMemo<ReactElement | null>(() => {
    if (attente === null) return null;
    return (
      <FenetreDesSorties
        attente={attente}
        ouverte={attenteOuverte}
        confirmer={() => {
          setAttenteOuverte(false);
          void appeler(attente.bouton, attente.ficheId, 'confirme');
        }}
        annuler={() => setAttenteOuverte(false)}
      />
    );
  }, [attente, attenteOuverte, appeler]);

  return useMemo(
    () => ({ etat, occupe, lancer, fenetres, avisAction, oublierLAvis }),
    [etat, occupe, lancer, fenetres, avisAction, oublierLAvis],
  );
}

/** UNE fenetre qui reunit la confirmation declaree et la liste des sorties hors de l'equipe (s'il y en a). */
function FenetreDesSorties({
  attente,
  ouverte,
  confirmer,
  annuler,
}: {
  attente: Attente;
  ouverte: boolean;
  confirmer: () => void;
  annuler: () => void;
}): JSX.Element {
  const fenetre = fenetreDesSorties(attente.bouton.label, attente.sorties, attente.bouton.confirmation);
  return (
    <ConfirmDialog
      open={ouverte}
      tone={fenetre.tone}
      title={fenetre.title}
      body={fenetre.body}
      confirmLabel={fenetre.confirmLabel}
      {...(fenetre.cancelLabel === undefined ? {} : { cancelLabel: fenetre.cancelLabel })}
      onConfirm={confirmer}
      onCancel={annuler}
    />
  );
}
